import { useState } from 'react';
import { Button } from './ui/button';
import {
  AlertTriangle,
  Building,
  Info,
  MapPin,
  Navigation,
  Phone,
  ClipboardCheck,
  UserPlus,
  Check,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslationWithLanguage } from '../context/language-context';
import { useAuth } from 'react-oidc-context';

// Get BASE_URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

// Dialog component for confirmation
const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  inspectionDetails,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  inspectionDetails: {
    establishment: string;
    type: string;
  };
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#00000080] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">{message}</p>

        <div className="bg-gray-50 p-3 rounded-md mb-4">
          <div className="mb-2">
            <span className="font-medium">Establishment:</span>{' '}
            {inspectionDetails.establishment}
          </div>
          <div>
            <span className="font-medium">Inspection Type:</span>{' '}
            {inspectionDetails.type}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-gray-300"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            className="bg-[#b89256] text-white hover:opacity-90"
            onClick={onConfirm}
          >
            <Check className="w-4 h-4 mr-2" />
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

import { createPortal } from 'react-dom';

// Toast component for notifications
const Toast = ({
  message,
  type = 'success',
  onClose,
}: {
  message: string;
  type?: 'success' | 'error';
  onClose: () => void;
}) => {
  // Use createPortal to render the toast at the root level
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[9999]">
      <div
        className={`rounded-md shadow-md p-4 flex items-center ${
          type === 'success'
            ? 'bg-green-50 border-l-4 border-green-500'
            : 'bg-red-50 border-l-4 border-red-500'
        }`}
      >
        <div
          className={`mr-3 ${
            type === 'success' ? 'text-green-500' : 'text-red-500'
          }`}
        >
          {type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
        </div>
        <div className="flex-1">
          <p
            className={`text-sm font-medium ${
              type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}
          >
            {message}
          </p>
        </div>
        <button
          onClick={onClose}
          className="ml-4 text-gray-400 hover:text-gray-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>,
    document.body
  );
};

// Define types for establishments/inspections
interface Establishment {
  id: string;
  name_en: string;
  name_ar: string;
}

interface InspectionType {
  id: number;
  name_en: string;
  name_ar: string;
}

interface Emirate {
  id: number;
  name_en: string;
  name_ar: string;
}

interface InspectionCardProps {
  id: string;
  establishment_id: string;
  establishment: Establishment;
  emirate: Emirate;
  inspection_type: InspectionType;
  risk_grade: string;
  risk_category?: string; // Added to support risk category display
  status: string;
  language: string;
  onSelect?: (id: string) => void;
  isSelected?: boolean;
  points?: number; // Optional points field
  context?: string; // New prop to determine where the card is being displayed
  showInSuggestedInspections?: boolean; // Alternative prop for context
  distance?: number; // Distance in km for route optimization
  groupCount?: number; // Number of inspections grouped together
  groupedItems?: any; // List of grouped inspection IDs
  assigned_by?: string; // Who assigned this task
  onUnassign?: (taskId: string) => Promise<void>; // Function to unassign a task
}

// Function to get risk color hex based on grade
const getRiskColorHex = (grade: string): string => {
    switch (grade) {
      case 'LOW': return '#00843d';
      case 'MEDIUM': return '#f97316';
      case 'HIGH': return '#c8102e';
      default: return '#b89256';
    }
  };
// Function to get current location from localStorage
const getCurrentLocation = async (): Promise<{ lat: number; long: number } | null> => {
  return new Promise((resolve) => {
    try {
      const locationString = localStorage.getItem('cL');
      if (locationString) {
        const parsedLocation = JSON.parse(locationString);
        // Ensure lat and lng exist and are numbers before using them
        if (parsedLocation && typeof parsedLocation.lat === 'number' && typeof parsedLocation.lng === 'number') {
          resolve({ lat: parsedLocation.lat, long: parsedLocation.lng }); // Map lng to long
        } else {
          console.warn('Parsed location data is invalid or missing lat/lng from localStorage.');
          resolve(null);
        }
      } else {
        console.warn('Location data ("cL") not found in localStorage.');
        resolve(null);
      }
    } catch (error) {
      console.error('Failed to get current location from localStorage:', error);
      resolve(null); // Resolve with null in case of any error
    }
  });
};

export const InspectionCard = ({
  id,
  establishment_id,
  establishment,
  emirate,
  inspection_type,
  risk_grade,
  risk_category,
  status,
  language,
  onSelect,
  isSelected = false,
  points = 1.1, // Default to 1.1 points as specified in requirements
  context,
  showInSuggestedInspections = false,
  distance,
  groupCount,
  groupedItems,
  assigned_by,
  onUnassign,
}: InspectionCardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslationWithLanguage();
  const auth = useAuth();

  // State for dialog and toast
  const [showDialog, setShowDialog] = useState(false);
  const [showUnassignDialog, setShowUnassignDialog] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>(
    'success'
  );
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // Function to handle self-assignment or start inspection
  const handleSelfAssign = async () => {
    console.log(`handleSelfAssign called with status: ${status}`);
    localStorage.setItem('taskIds', JSON.stringify(groupedItems));
    console.log('groupedItems:', groupedItems[0]);
    // Split the id by underscore to handle multiple task IDs
    const taskIds = id.split('_');

    // For 'assigned' status, call the start API and navigate to Active tab
    if (status === 'assigned') {
      setIsAssigning(true);
      try {
        // Get current location
        let locationData = null;
        try {
          const location = localStorage.getItem('cL')
          locationData = JSON.parse(location || '{}');
          console.log('Current location for starting task:', locationData);
        } catch (locationError) {
          console.warn('Failed to get location for starting task:', locationError);
          // Continue without location data
          console.log('Proceeding with task start without location data');
        }

        // Call the API for each task ID
        for (const taskId of taskIds) {
          const requestBody: any = {
            task_id: taskId,
          };

          // Add location data if available
          if (locationData && locationData.lat && locationData.lng) {
            requestBody.location = {lat: locationData.lat, long: locationData.lng};
            console.log('Adding location data to request:', locationData);
          } else {
            console.log('No valid location data available for request');
          }

          const response = await fetch(
            `${BASE_URL}/sdp/rules/api/v2/task/actions/start`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${auth?.user?.access_token}`,
              },
              body: JSON.stringify(requestBody),
            }
          );
          const data = await response.json();
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          if (!data.status) {
            throw new Error(data.message || 'Failed to start inspection');
          }
        }
        // Show success toast after all tasks are processed
        setToastMessage(t('common.startedSuccessfully'));
        setToastType('success');
        setShowToast(true);
        // Navigate to the inspection details page using the first task ID
        localStorage.setItem('taskIds', JSON.stringify(groupedItems));
        localStorage.setItem('tid', taskIds[0]);
        navigate(`/establishment/${taskIds[0]}`); ///questionnaire
      } catch (error) {
        console.error('Failed to start inspection:', error);
        setToastMessage(
          error instanceof Error ? error.message : t('common.startFailed')
        );
        setToastType('error');
        setShowToast(true);
      } finally {
        setIsAssigning(false);
      }
      return;
    }

    // For 'inProgress' status, navigate directly to the inspection details page
    if (status === 'inProgress') {
      console.log('Handling inProgress status, navigating to details page');
      localStorage.setItem('tid', taskIds[0]);
      localStorage.setItem('taskIds', JSON.stringify(groupedItems));
      navigate(`/establishment/${taskIds[0]}`); ///questionnaire
      return;
    }

    // For 'all' or 'pool' tabs, show confirmation dialog for assignment
    if (status !== 'assigned' && status !== 'inProgress') {
      setShowDialog(true);
    }
  };

  // Function to confirm assignment
  const confirmAssignment = async () => {
    try {
      setIsAssigning(true);

      // Close the dialog immediately to show loading state on the button
      setShowDialog(false);

      // Get current location
      let locationData = null;
      try {
        locationData = await getCurrentLocation();
      } catch (locationError) {
        console.warn('Failed to get location for assignment:', locationError);
        // Continue without location data
        console.log('Proceeding with assignment without location data');
      }

      // Prepare request body
      const requestBody: any = {
        task_id: (id.indexOf('_') !== -1 ? id.split('_') : id)
      };

      // Add location data if available
      if (locationData && locationData.lat && locationData.long) {
        requestBody.location = locationData;
        console.log('Adding location data to assignment request:', locationData);
      } else {
        console.log('No valid location data available for assignment request');
      }

      // Call the API to assign the task
      const response = await fetch(
        `${BASE_URL}/sdp/rules/api/v2/task/actions/self_assign`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.user?.access_token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      // Parse the response
      const data = await response.json();

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Check if the response indicates success
      if (data.status === true || response.ok) {
        // Show success toast
        setToastMessage(t('common.assignedSuccessfully'));
        setToastType('success');
        setShowToast(true);

        // Navigate to assigned tab and refresh
        // This will be handled by the parent component
        if (window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent('inspection:assigned', {
              detail: { id, status: 'assigned' },
            })
          );
        }
      } else {
        // If the API returned a 200 response but with status false
        throw new Error(data.message || 'Assignment failed');
      }
    } catch (error) {
      console.error('Failed to assign task:', error);

      // Show error toast
      setToastMessage(
        error instanceof Error
          ? error.message
          : t('common.assignmentFailed')
      );
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsAssigning(false);
    }
  };

  // Function to handle unassign action
  const handleUnassign = async () => {
    if (!onUnassign) return;

    try {
      setIsUnassigning(true);
      setShowUnassignDialog(false);

      // Call the parent's unassign function
      await onUnassign(id);

      // Show success toast
      setToastMessage('Task unassigned successfully');
      setToastType('success');
      setShowToast(true);

    } catch (error) {
      console.error('Failed to unassign task:', error);

      // Show error toast
      setToastMessage(
        error instanceof Error
          ? error.message
          : 'Failed to unassign task'
      );
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleCardClick = () => {
    if (onSelect) {
      onSelect(id);
    }
  };

  return (
    <div
      className={`rounded-lg overflow-hidden transition-all duration-200 relative ${
        isSelected ? 'border bg-gold-50 border-[#b89256] shadow-lg' : 'border border-gray-200 '
      }`}
      onClick={handleCardClick}
    >
      {/* Multiselect checkbox - only shown in SuggestedInspections context but visually hidden */}
      {(context === 'suggestedInspections' ||
        showInSuggestedInspections) &&
        onSelect && (
          <div
            className="sr-only"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`w-5 h-5 rounded border ${
                isSelected
                  ? 'bg-[#b89256] border-[#b89256]'
                  : 'bg-white border-gray-300'
              } flex items-center justify-center cursor-pointer`}
              onClick={() => onSelect(id)}
            >
              {isSelected && <Check className="w-3 h-3 text-white" />}
            </div>
          </div>
        )}
      {/* Risk gradient border at top */}
      {/* <div
        className="absolute top-0 left-0 right-0 h-1.5"
        style={{
          background: `linear-gradient(to left, #b89256}, #b89256CC, #b8925699)`
        }}
      ></div> */}
      {/* (to left, ${getRiskColorHex(risk_grade)}, ${getRiskColorHex(risk_grade)}CC, ${getRiskColorHex(risk_grade)}99) */}


      <div
        className="absolute top-0 left-0 right-0 h-1.5"
        style={{
          background: `linear-gradient(to left, #b89256, #b892569c, #b89256fc) `
        }}
      ></div>

      {/* Risk label */}
      <div
        className={`absolute top-4 px-1 py-1 rounded-full text-xs font-bold ${language === 'ar' ? 'left-4' : 'right-4'}`}
        style={{
          backgroundColor: `${getRiskColorHex(risk_grade)}`,
          color: `${getRiskColorHex(risk_grade)}`,
          border: `1px solid ${getRiskColorHex(risk_grade)}`,
          boxShadow: `0 0px 0px ${getRiskColorHex(risk_grade)}`
        }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
        {/* {risk_category ? risk_category.toUpperCase() :
          risk_grade === 'A' || risk_grade === 'B' ? 'LOW' :
          risk_grade === 'C' || risk_grade === 'D' ? 'MEDIUM' :
          'HIGH'} */}
      </div>

      <div className="p-4">
        {/* Establishment Icon and Name */}
        <div className="flex items-start mb-2">
          <Building className="w-5 h-5 text-gray-600 mr-2 mt-0.5 flex-shrink-0" />
          <div className="flex-1 max-w-full overflow-hidden">
            <h3
              className="text-base w-full block truncate pr-12"
              title={language === 'en' ? establishment.name_en : establishment.name_ar}
            >
              {language === 'en' ? establishment.name_en : establishment.name_ar}
            </h3>

            {/* Location and Points - Small text */}
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <MapPin className="w-3 h-3 mr-1" />
              <span className="mr-2">{language === 'en' ? emirate.name_en : emirate.name_ar}</span>
              <span className="flex items-center">
                <span className="mr-1">•</span>
                <span>{points.toFixed(2)} pts</span>
                {groupCount && groupCount > 1 && (
                  <span className="ml-2 bg-[#b89256] text-white px-1.5 py-0.5 rounded-full text-[10px] font-medium">
                    {groupCount} tasks
                  </span>
                )}
              </span>
            </div>

            {/* Establishment ID */}
            <p className="text-xs text-gray-500 mt-1">
              Est. no: {establishment_id}
            </p>
          </div>
        </div>

        {/* Inspection Type as Badge */}
        <div className="my-1 ml-3">
          <span
            className="ml-2 inline-block px-2 py-1 rounded-md text-xs font-medium"
            // style={{
            //   backgroundColor: `${getRiskColorHex(risk_grade)}22`,
            //   color: getRiskColorHex(risk_grade),
            //   border: `1px solid ${getRiskColorHex(risk_grade)}44`
            // }}
          >
            {language === 'en'
              ? (inspection_type.name_en || 'Regular Inspection')
              : (inspection_type.name_ar || 'تفتيش منتظم')}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center ml-4">
          <div className="flex space-x-2 ml-2" style={{ gap: '0.5rem' }}>
            <Button
              variant="outline"
              size="icon"
              className="rounded-lg h-9 w-9 p-0 border border-gray-300"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                localStorage.setItem('tid', id);
                localStorage.setItem('taskIds', JSON.stringify(groupedItems));
                navigate(`/establishment/${establishment_id}`);
              }}
            >
              <Info className="w-4 h-4 text-gray-600" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className={`rounded-lg h-9 ${
                distance !== undefined ? 'px-2 w-auto' : 'w-9 p-0'
              } border border-gray-300 flex items-center`}
              title={
                distance !== undefined
                  ? `Distance: ${distance.toFixed(1)} km`
                  : 'Navigate'
              }
            >
              <Navigation className="w-4 h-4 text-gray-600" />
              {distance !== undefined && (
                <span className="ml-1 text-xs font-medium text-gray-700">
                  {distance.toFixed(1)} km
                </span>
              )}
            </Button>
            {/* <Button
              variant="outline"
              size="icon"
              className="rounded-full h-9 w-9 p-0 border border-gray-300"
            >
              <Phone className="w-4 h-4 text-gray-600" />
            </Button> */}
          </div>
          {/* Only show assign button when not in SuggestedInspections context or explicitly in that context */}
          {(!context ||
            context === 'suggestedInspections' ||
            showInSuggestedInspections) && (
            <div className="flex space-x-2">
              {/* Only show unassign button for tasks assigned by self */}
              {assigned_by === 'self' && status === 'assigned' && (
                <Button
                  className="rounded-lg border border-gold-300 text-gold-500 hover:bg-red-50 h-9 text-sm font-medium w-[100px]"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setShowUnassignDialog(true); // Show confirmation dialog
                  }}
                  disabled={isUnassigning}
                >
                  {isUnassigning ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('common.loading')}
                    </span>
                  ) : (
                    t('common.unassign')
                  )}
                </Button>
              )}
            <Button
              className="rounded-lg bg-[#b89256] text-white hover:opacity-90 h-9 text-sm font-medium w-[100px]"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleSelfAssign();
              }}
              disabled={isAssigning}
            >
              {isAssigning ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('common.loading')}
                </span>
              ) : status === 'assigned' || status === 'inProgress' ? (
                t('common.start')
              ) : (
                t('common.assign')
              )}
            </Button>


              </div>
          )}

          {/* Assignment Confirmation Dialog */}
          {showDialog && (
            <ConfirmationDialog
              isOpen={showDialog}
              onClose={() => setShowDialog(false)}
              onConfirm={confirmAssignment}
              title={t('common.confirmAssignment')}
              message={t('common.assignmentConfirmMessage')}
              inspectionDetails={{
                establishment:
                  language === 'en'
                    ? establishment.name_en
                    : establishment.name_ar,
                type:
                  language === 'en'
                    ? inspection_type.name_en
                    : inspection_type.name_ar,
              }}
            />
          )}

          {/* Unassignment Confirmation Dialog */}
          {showUnassignDialog && (
            <ConfirmationDialog
              isOpen={showUnassignDialog}
              onClose={() => setShowUnassignDialog(false)}
              onConfirm={handleUnassign}
              title={t('common.confirmUnassignment') || "Confirm Unassignment"}
              message={t('common.unassignmentConfirmMessage') || "Are you sure you want to unassign this task? This action cannot be undone."}
              inspectionDetails={{
                establishment:
                  language === 'en'
                    ? establishment.name_en
                    : establishment.name_ar,
                type:
                  language === 'en'
                    ? inspection_type.name_en
                    : inspection_type.name_ar,
              }}
            />
          )}

          {/* Toast Notification */}
          {showToast && (
            <Toast
              message={toastMessage}
              type={toastType}
              onClose={() => setShowToast(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
